/**
 * The matcher service HTTP contract.
 *
 * Consumed by the web app (through a Vite proxy in dev) and by Cloud Functions.
 * Implemented by `services/matcher` in Python, whose Pydantic models are generated
 * from these schemas' JSON Schema output and round-trip tested against them, so a
 * field rename here fails the Python test suite rather than producing a 422 in
 * front of a user.
 */
import { z } from 'zod'
import { CandidateSchema, CriterionEvaluationSchema } from './match.js'
import { StructuredCriterionSchema } from './criteria.js'
import { PatientSchema } from './patient.js'
import { MatchBandSchema, PurposeOfUseSchema, SexSchema } from './vocab.js'
import { IsoInstantSchema, ProbabilitySchema, idSchema } from './zod-util.js'
import type { ModelVersion, OrgId, PatientId, TrialId } from './ids.js'

/** Base path the service is mounted at, behind the Vite dev proxy. */
export const MATCHER_BASE_PATH = '/api/matcher'

export const MATCHER_ROUTES = {
  health: '/health',
  modelInfo: '/model/info',
  matchPatient: '/match/patient',
  matchTrial: '/match/trial',
  cohortCount: '/cohort/count',
  parseCriteria: '/criteria/parse',
  explain: '/explain',
} as const

// ---------------------------------------------------------------------------
// Shared request pieces
// ---------------------------------------------------------------------------

/**
 * Purpose and actor, required on every request that touches a patient record.
 *
 * Carried in the body rather than inferred server-side because the matcher is a
 * stateless service behind a trusted caller: the *caller* knows why it is asking,
 * and that reason has to reach the audit log. A request with no stated purpose is
 * rejected rather than defaulted, since a default purpose would make the whole
 * audit trail meaningless.
 */
export const RequestContextSchema = z.object({
  actorUserId: z.string().min(1),
  actorRole: z.string().min(1),
  purposeOfUse: PurposeOfUseSchema,
  /** Idempotency/correlation key, echoed in the response and written to the audit log. */
  requestId: z.string().min(1),
})
export type RequestContext = z.infer<typeof RequestContextSchema>

/** Knobs on the pipeline, all optional with production-sane defaults. */
export const MatchOptionsSchema = z.object({
  /** How many candidates to return after reranking. */
  topK: z.number().int().min(1).max(200).default(25),
  /** How many candidates stage 1 retrieves before reranking. */
  retrievalK: z.number().int().min(1).max(2000).default(200),
  /** Maximum straight-line distance to a site, in km. Generous by default; see D-09. */
  maxDistanceKm: z.number().positive().max(20000).default(500),
  /** Include trials that are not currently recruiting. Off by default. */
  includeNonRecruiting: z.boolean().default(false),
  /**
   * Run stage 3 (per-criterion adjudication with evidence). Expensive, so it is
   * opt-in: list views do not need it, and a single-candidate detail view does.
   */
  adjudicate: z.boolean().default(false),
  /** Minimum calibrated probability to return at all. */
  minProbability: ProbabilitySchema.default(0),
  /** Include SHAP feature attributions. Adds latency; needed only for the detail view. */
  explain: z.boolean().default(false),
})
export type MatchOptions = z.infer<typeof MatchOptionsSchema>

// ---------------------------------------------------------------------------
// POST /match/patient — one patient, many trials
// ---------------------------------------------------------------------------

/**
 * Either a stored patient ID or an inline record.
 *
 * Inline exists for two real cases: the patient view's "check my eligibility"
 * questionnaire, where the person has typed facts that are not in any chart, and
 * evaluation runs over a fixture set that is deliberately not in the database.
 */
export const MatchPatientRequestSchema = z.object({
  context: RequestContextSchema,
  patientId: idSchema<PatientId>('patient id').optional(),
  patient: PatientSchema.optional(),
  /** Restrict to specific trials. Used by the "re-score this one trial" action. */
  trialIds: z.array(idSchema<TrialId>('trial id')).optional(),
  options: MatchOptionsSchema.partial().optional(),
}).refine((r) => Boolean(r.patientId) !== Boolean(r.patient), {
  message: 'Provide exactly one of patientId or patient',
})
export type MatchPatientRequest = z.infer<typeof MatchPatientRequestSchema>

/** Per-stage timings and counts, returned on every match response. */
export const PipelineDiagnosticsSchema = z.object({
  trialsConsidered: z.number().int().nonnegative(),
  trialsAfterHardFilters: z.number().int().nonnegative(),
  trialsRetrieved: z.number().int().nonnegative(),
  trialsReranked: z.number().int().nonnegative(),
  trialsAdjudicated: z.number().int().nonnegative(),
  /**
   * Trials dropped by each hard filter, keyed by filter name.
   *
   * Exposed rather than hidden because a hard filter is the one place a patient
   * can be lost invisibly. Surfacing the counts makes an over-aggressive filter
   * something a developer can see in a response instead of something a patient
   * silently never hears about. See D-09.
   */
  droppedByFilter: z.record(z.string(), z.number().int().nonnegative()).default({}),
  timings: z.object({
    filterMs: z.number().nonnegative(),
    retrievalMs: z.number().nonnegative(),
    rerankMs: z.number().nonnegative(),
    adjudicateMs: z.number().nonnegative(),
    totalMs: z.number().nonnegative(),
  }),
})
export type PipelineDiagnostics = z.infer<typeof PipelineDiagnosticsSchema>

export const MatchPatientResponseSchema = z.object({
  requestId: z.string(),
  patientId: idSchema<PatientId>('patient id').optional(),
  candidates: z.array(CandidateSchema),
  diagnostics: PipelineDiagnosticsSchema,
  modelVersion: idSchema<ModelVersion>('model version'),
  /** Non-fatal problems: a trial whose criteria failed to parse, a missing embedding. */
  warnings: z.array(z.string()).default([]),
  generatedAt: IsoInstantSchema,
})
export type MatchPatientResponse = z.infer<typeof MatchPatientResponseSchema>

// ---------------------------------------------------------------------------
// POST /match/trial — one trial, many patients
// ---------------------------------------------------------------------------

/**
 * The coordinator's direction of travel: given a trial, who in our population
 * might fit? Same pipeline, transposed.
 */
export const MatchTrialRequestSchema = z.object({
  context: RequestContextSchema,
  trialId: idSchema<TrialId>('trial id'),
  /** Restrict to one health system's patients. Required for anyone but a platform admin. */
  orgId: idSchema<OrgId>('org id').optional(),
  /** Restrict to a clinician's own panel. */
  careTeamUserId: z.string().optional(),
  options: MatchOptionsSchema.partial().optional(),
})
export type MatchTrialRequest = z.infer<typeof MatchTrialRequestSchema>

export const MatchTrialResponseSchema = z.object({
  requestId: z.string(),
  trialId: idSchema<TrialId>('trial id'),
  candidates: z.array(CandidateSchema),
  diagnostics: PipelineDiagnosticsSchema,
  modelVersion: idSchema<ModelVersion>('model version'),
  warnings: z.array(z.string()).default([]),
  generatedAt: IsoInstantSchema,
})
export type MatchTrialResponse = z.infer<typeof MatchTrialResponseSchema>

// ---------------------------------------------------------------------------
// POST /cohort/count — feasibility, de-identified
// ---------------------------------------------------------------------------

/**
 * The sponsor's question: if I write the criteria this way, how many patients
 * qualify, and where are they?
 *
 * Takes either a trial ID or a draft criteria list, so an analyst can test a
 * *hypothetical* protocol before it exists. That is the feature that makes this
 * view worth opening: it turns "we wrote 30 criteria and enrolled nobody" into a
 * question answerable before the protocol is finalised.
 */
export const CohortCountRequestSchema = z.object({
  context: RequestContextSchema,
  trialId: idSchema<TrialId>('trial id').optional(),
  /** A draft criteria set, for what-if analysis on a protocol not yet registered. */
  draftCriteria: z.array(StructuredCriterionSchema).optional(),
  minAgeYears: z.number().min(0).max(130).optional(),
  maxAgeYears: z.number().min(0).max(130).optional(),
  sex: SexSchema.optional(),
  /** Restrict to these health systems. Empty means the whole network. */
  orgIds: z.array(idSchema<OrgId>('org id')).default([]),
  /** Probability floor for counting someone as a candidate. */
  minProbability: ProbabilitySchema.default(0.4),
})
export type CohortCountRequest = z.infer<typeof CohortCountRequestSchema>

/**
 * Aggregate-only response.
 *
 * Every count below `SMALL_CELL_THRESHOLD` is returned as the string `"<11"`
 * rather than a number. Small cells are the standard re-identification vector — a
 * count of 1 in a ZIP3 for a rare condition names a person — so suppression
 * happens in the service, before serialization, where no caller can forget it.
 */
export const CohortCountResponseSchema = z.object({
  requestId: z.string(),
  /** Total matching patients, suppressed if small. */
  total: z.union([z.number().int().nonnegative(), z.literal('<11')]),
  bySite: z.array(
    z.object({
      orgId: z.string(),
      orgName: z.string(),
      city: z.string(),
      state: z.string(),
      count: z.union([z.number().int().nonnegative(), z.literal('<11')]),
    })
  ),
  byAgeBand: z.record(z.string(), z.union([z.number().int().nonnegative(), z.literal('<11')])),
  bySex: z.record(z.string(), z.union([z.number().int().nonnegative(), z.literal('<11')])),
  byBand: z.record(MatchBandSchema, z.union([z.number().int().nonnegative(), z.literal('<11')])),
  /**
   * Which criteria disqualify the most patients, and how many each would recover
   * if relaxed. This is the actionable output — it converts feasibility from a
   * verdict into a list of protocol edits with quantified payoffs.
   */
  limitingCriteria: z.array(
    z.object({
      criterionId: z.string(),
      criterionText: z.string(),
      excludedCount: z.union([z.number().int().nonnegative(), z.literal('<11')]),
      /** Extra patients who would qualify if this single criterion were dropped. */
      recoverableIfRelaxed: z.union([z.number().int().nonnegative(), z.literal('<11')]),
    })
  ),
  generatedAt: IsoInstantSchema,
})
export type CohortCountResponse = z.infer<typeof CohortCountResponseSchema>

/**
 * Cell-suppression threshold. 11 is the conventional floor used by HHS and CMS for
 * public-use health data releases.
 */
export const SMALL_CELL_THRESHOLD = 11

/** Apply small-cell suppression. The only sanctioned way to emit a count. */
export function suppressSmallCell(count: number): number | '<11' {
  return count > 0 && count < SMALL_CELL_THRESHOLD ? '<11' : count
}

/** Render a possibly-suppressed count for display. */
export function formatCount(count: number | '<11'): string {
  return count === '<11' ? 'fewer than 11' : count.toLocaleString('en-US')
}

// ---------------------------------------------------------------------------
// POST /criteria/parse — free text in, structure out
// ---------------------------------------------------------------------------

export const ParseCriteriaRequestSchema = z.object({
  context: RequestContextSchema.partial({ purposeOfUse: true }).extend({
    purposeOfUse: PurposeOfUseSchema.default('OPERATIONS'),
  }),
  trialId: idSchema<TrialId>('trial id'),
  /** The raw eligibility blob. */
  rawText: z.string().min(1),
  /** Use the LLM structurer in addition to the rule parser. */
  useLlm: z.boolean().default(false),
})
export type ParseCriteriaRequest = z.infer<typeof ParseCriteriaRequestSchema>

export const ParseCriteriaResponseSchema = z.object({
  requestId: z.string(),
  trialId: idSchema<TrialId>('trial id'),
  criteria: z.array(StructuredCriterionSchema),
  unparsedSegments: z.array(z.object({ text: z.string(), reason: z.string() })),
  coverage: ProbabilitySchema,
  parserVersion: z.string(),
})
export type ParseCriteriaResponse = z.infer<typeof ParseCriteriaResponseSchema>

// ---------------------------------------------------------------------------
// POST /explain — adjudicate one pair in detail
// ---------------------------------------------------------------------------

export const ExplainRequestSchema = z.object({
  context: RequestContextSchema,
  patientId: idSchema<PatientId>('patient id'),
  trialId: idSchema<TrialId>('trial id'),
  /** Re-run stage 3 even if a cached adjudication exists. */
  force: z.boolean().default(false),
})
export type ExplainRequest = z.infer<typeof ExplainRequestSchema>

export const ExplainResponseSchema = z.object({
  requestId: z.string(),
  candidate: CandidateSchema,
  evaluations: z.array(CriterionEvaluationSchema),
  /** Plain-language summary for a clinician, two or three sentences. */
  narrative: z.string(),
  /** Whether an LLM was involved, so the UI can label AI-generated text as such. */
  usedLlm: z.boolean(),
  generatedAt: IsoInstantSchema,
})
export type ExplainResponse = z.infer<typeof ExplainResponseSchema>

// ---------------------------------------------------------------------------
// GET /health and /model/info
// ---------------------------------------------------------------------------

export const HealthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded', 'error']),
  version: z.string(),
  /** Which optional components actually loaded. Degraded is normal and fine. */
  components: z.record(z.string(), z.enum(['ok', 'unavailable'])),
  trialsIndexed: z.number().int().nonnegative(),
})
export type HealthResponse = z.infer<typeof HealthResponseSchema>

/**
 * Model card, served at runtime.
 *
 * Returned by the API and rendered in the admin view rather than living only in a
 * README, because the numbers a user should judge a prediction by must travel with
 * the model that is actually loaded — a README describes whatever was true when
 * someone last edited it.
 */
export const ModelInfoResponseSchema = z.object({
  modelVersion: idSchema<ModelVersion>('model version'),
  trainedAt: IsoInstantSchema,
  rankerBackend: z.enum(['sklearn_hgb', 'lightgbm_lambdarank', 'logistic_baseline', 'rules_only']),
  retrievalBackend: z.enum(['tfidf_svd', 'sentence_transformer', 'bm25_only', 'hybrid']),
  featureCount: z.number().int().positive(),
  featureNames: z.array(z.string()),
  trainingRows: z.number().int().nonnegative(),
  /** Held-out metrics. Named exactly as in the evaluation report so they can be cross-checked. */
  metrics: z.record(z.string(), z.number()),
  /** Subgroup recall gaps, surfaced deliberately: a hidden fairness metric is not a metric. */
  subgroupRecall: z.record(z.string(), z.number()).default({}),
  /** Honest statement of what the model was trained on. Rendered verbatim in the UI. */
  dataProvenance: z.string(),
  limitations: z.array(z.string()).default([]),
})
export type ModelInfoResponse = z.infer<typeof ModelInfoResponseSchema>

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.enum([
      'BAD_REQUEST',
      'UNAUTHORIZED',
      'FORBIDDEN',
      'NOT_FOUND',
      'CONSENT_REQUIRED',
      'MODEL_UNAVAILABLE',
      'RATE_LIMITED',
      'INTERNAL',
    ]),
    message: z.string(),
    /** Field-level detail for validation failures. */
    fields: z.record(z.string(), z.string()).optional(),
    requestId: z.string().optional(),
  }),
})
export type ApiError = z.infer<typeof ApiErrorSchema>
